import { useEconomyStore } from '@/state/useEconomyStore'
import { buyFromMarket, getMarketPrices } from '@/economy/api'
import { PUB_ITEMS } from '@/economy/recipes'
import { ITEM_DISPLAY_NAMES } from '@/economy/types'
import type { WalletInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

const MENU_DESCRIPTIONS: Partial<Record<ItemType, string>> = {
  bread: 'Fresh from the oven. Restores vitality.',
  ale: 'Strong village brew. Warms the bones.',
  wine: 'Fine vintage from local vineyards.',
  mutton: 'Hearty roasted mutton leg.',
  poultry: 'Herb-roasted fowl.',
}

export function PubPanel({ interaction, wallet }: PanelProps) {
  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const tradeLoading = useEconomyStore((s) => s.tradeLoading)
  const tradeError = useEconomyStore((s) => s.tradeError)
  const setTradeLoading = useEconomyStore((s) => s.setTradeLoading)
  const setTradeError = useEconomyStore((s) => s.setTradeError)
  const updateCargoFromTrade = useEconomyStore((s) => s.updateCargoFromTrade)
  const setMarketPrices = useEconomyStore((s) => s.setMarketPrices)

  const prices = villageData?.marketPrices ?? []
  const priceMap = new Map(prices.map((p) => [p.item, p]))

  async function handleBuy(item: ItemType) {
    if (!interaction.serverId) return
    const price = priceMap.get(item)
    if (!price) return
    setTradeLoading(true)
    setTradeError(null)
    try {
      const maxPrice = Math.ceil(price.spot_price * 1.3)
      const result = await buyFromMarket(interaction.serverId, item, 1, maxPrice)
      updateCargoFromTrade(item, result.new_item_qty, result.new_follis_balance)
      const newPrices = await getMarketPrices(interaction.serverId)
      setMarketPrices(interaction.villageIndex, newPrices)
    } catch (e: any) {
      setTradeError(e.message || 'Purchase failed')
    }
    setTradeLoading(false)
  }

  return (
    <>
      {tradeError && <div className="panel-error">{tradeError}</div>}

      <div className="panel-section">
        <h3 className="panel-section__title">Menu</h3>
        {PUB_ITEMS.map((item) => {
          const price = priceMap.get(item)
          const available = price && price.pool_item_reserve >= 1
          return (
            <div key={item} className="panel-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="panel-row__label">{ITEM_DISPLAY_NAMES[item]}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="panel-row__value">{price ? `${price.spot_price.toFixed(1)}ƒ` : '—'}</span>
                  <button
                    className="panel-btn"
                    disabled={tradeLoading || !available || !wallet || (price && wallet.follis < price.spot_price)}
                    onClick={() => handleBuy(item)}
                  >
                    Order
                  </button>
                </span>
              </div>
              <p style={{ color: '#6b6050', fontFamily: 'Crimson Text, serif', fontSize: 12, margin: 0 }}>
                {MENU_DESCRIPTIONS[item] || ''}
              </p>
            </div>
          )
        })}
      </div>

      <div className="panel-section">
        <h3 className="panel-section__title">Rumors</h3>
        <p style={{ color: '#8b7355', fontFamily: 'Crimson Text, serif', fontSize: 13, lineHeight: 1.6, fontStyle: 'italic' }}>
          "I heard the mining villages up north are desperate for wheat. Could fetch a fine price there..."
        </p>
      </div>
    </>
  )
}
